import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchConsultationsAPI,
  fetchConsultationByIdAPI,
  deleteConsultationAPI,
  updateConsultationAPI,
} from "../../services/consultationAPI";

// ✅ Fetch all consultations with pagination
export const fetchConsultations = createAsyncThunk(
  "consultations/fetchConsultations",
  async (page = 1) => {
    const response = await fetchConsultationsAPI(page);
    // Handle different response structures
    return {
      data: response.data.data || response.data,
      pagination: response.data.pagination || null
    };
  }
);

// ✅ Fetch single consultation by ID
export const fetchConsultationById = createAsyncThunk(
  "consultations/fetchConsultationById",
  async (id) => {
    const response = await fetchConsultationByIdAPI(id);
    return response.data;
  }
);

// ✅ Delete consultation
export const deleteConsultation = createAsyncThunk(
  "consultations/deleteConsultation",
  async (id) => {
    await deleteConsultationAPI(id);
    return id;
  }
);

// ✅ Update consultation
export const updateConsultation = createAsyncThunk(
  "consultations/updateConsultation",
  async ({ id, data }) => {
    const response = await updateConsultationAPI(id, data);
    return response.data;
  }
);

const initialState = {
  allItems: [], // Store all data
  items: [], // Display items (paginated)
  detail: null,
  status: "idle", // for list (fetchConsultations)
  detailStatus: "idle", // for fetchConsultationById
  error: null,
  // Pagination state
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  }
};

const consultationSlice = createSlice({
  name: "consultations",
  initialState,
  reducers: {
    clearDetail: (state) => {
      state.detail = null;
      state.detailStatus = "idle";
    },
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
      // Update displayed items based on current page
      const startIndex = (action.payload - 1) * state.pagination.itemsPerPage;
      const endIndex = startIndex + state.pagination.itemsPerPage;
      state.items = state.allItems.slice(startIndex, endIndex);
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ Fetch all
      .addCase(fetchConsultations.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchConsultations.fulfilled, (state, action) => {
        state.status = "succeeded";
        const { data } = action.payload;
        const appointments = data.appointments || data; // Handle different response structures

        // Store all data
        state.allItems = appointments.map(apt => {
          const d = new Date(apt.scheduled_at);
          const dateStr = `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`;
          const hour12 = (d.getHours() % 12).toString().padStart(2,'0');
          const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
          const timeStr = `${hour12}:${d.getMinutes().toString().padStart(2,'0')}:${d.getSeconds().toString().padStart(2,'0')} ${ampm}`;
          return {
            uuid: apt.uuid,
            petParent: [apt.booker.first_name, apt.booker.last_name].filter(Boolean).join(' ') || 'NA',
            pet: apt.pet.name,
            breed: apt.pet.breed || 'NA',
            veterinarian: [apt.provider.first_name, apt.provider.last_name].filter(Boolean).join(' ') || 'NA',
            datetime: `${dateStr} ${timeStr}`,
            type: apt.service_provider.name || 'NA',
            status: apt.status,
            notes: apt.notes,
            // use for image if needed
            petImage: apt.pet.profile_image_url,
          }
        });

        // Calculate pagination based on all items
        const totalItems = state.allItems.length;
        const itemsPerPage = state.pagination.itemsPerPage;
        const totalPages = Math.ceil(totalItems / itemsPerPage);

        // Update pagination
        state.pagination = {
          currentPage: 1, // Reset to page 1 when new data is loaded
          totalPages: totalPages || 1,
          totalItems: totalItems,
          itemsPerPage: itemsPerPage
        };

        // Set initial displayed items (first page)
        state.items = state.allItems.slice(0, itemsPerPage);
      })
      .addCase(fetchConsultations.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      // ✅ Fetch single
      .addCase(fetchConsultationById.pending, (state) => {
        state.detailStatus = "loading";
        state.detail = null;
      })
      .addCase(fetchConsultationById.fulfilled, (state, action) => {
        state.detailStatus = "succeeded";
        state.detail = action.payload.data;
      })
      .addCase(fetchConsultationById.rejected, (state, action) => {
        state.detailStatus = "failed";
        state.error = action.error.message;
      })

      // ✅ Delete
      .addCase(deleteConsultation.fulfilled, (state, action) => {
        const id = action.payload;
        state.items = state.items.filter((c) => c.uuid !== id);
        if (state.detail?.id === id) { // ?? if detail uses id
          state.detail = null;
          state.detailStatus = "idle";
        }
      })

      // ✅ Update
      .addCase(updateConsultation.fulfilled, (state, action) => {
        state.detail = action.payload; // refresh detail
        state.items = state.items.map((c) =>
          c.id === action.payload.id ? action.payload : c
        );
      });
  },
});

export const { clearDetail, setCurrentPage } = consultationSlice.actions;
export default consultationSlice.reducer;
